import { useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, TableIcon, ArrowUpDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { getGroupPerformance } from './mockData';
import { cn } from '@/lib/utils';

type SortKey = 'paperCount' | 'avgIterations' | 'avgReviewScore' | 'reviewsUsed';

function ScoreBadge({ score }: { score: number }) {
  if (score === 0) return <span className="text-muted-foreground">—</span>;
  const color =
    score >= 7.5
      ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
      : score >= 5
        ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
        : 'bg-destructive/10 text-destructive';
  return (
    <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold', color)}>
      {score.toFixed(1)}
    </span>
  );
}

const GroupPerformancePanel = () => {
  const [view, setView] = useState<'table' | 'chart'>('table');
  const [sortKey, setSortKey] = useState<SortKey>('avgReviewScore');
  const [sortAsc, setSortAsc] = useState(false);

  const data = getGroupPerformance();

  const sorted = [...data].sort((a, b) => {
    const diff = (a[sortKey] as number) - (b[sortKey] as number);
    return sortAsc ? diff : -diff;
  });

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  };

  const SortHeader = ({ label, k }: { label: string; k: SortKey }) => (
    <button
      onClick={() => handleSort(k)}
      className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
    >
      {label}
      <ArrowUpDown className={cn('h-3 w-3', sortKey === k ? 'text-primary' : 'text-muted-foreground/50')} />
    </button>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.4 }}
      className="rounded-2xl border border-border/50 bg-card/80 backdrop-blur-xl shadow-lg overflow-hidden"
    >
      <div className="flex items-center justify-between p-6 pb-4">
        <h3 className="text-lg font-semibold text-foreground">Gruppen-Performance</h3>
        <div className="flex gap-1 rounded-lg border border-border p-0.5">
          <Button
            variant={view === 'table' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setView('table')}
            className="h-7 px-2"
          >
            <TableIcon className="h-4 w-4" />
          </Button>
          <Button
            variant={view === 'chart' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setView('chart')}
            className="h-7 px-2"
          >
            <BarChart3 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {view === 'table' ? (
        <div className="px-6 pb-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Gruppe</TableHead>
                <TableHead className="text-right">
                  <SortHeader label="Paper" k="paperCount" />
                </TableHead>
                <TableHead className="text-right">
                  <SortHeader label="Ø Iterationen" k="avgIterations" />
                </TableHead>
                <TableHead className="text-right">
                  <SortHeader label="Ø Score" k="avgReviewScore" />
                </TableHead>
                <TableHead className="text-right">
                  <SortHeader label="Reviews" k="reviewsUsed" />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((g) => (
                <TableRow key={g.groupId}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div
                        className="h-3 w-3 rounded-full shrink-0"
                        style={{ backgroundColor: g.color }}
                      />
                      <span className="font-medium">{g.groupName}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-medium">{g.paperCount}</TableCell>
                  <TableCell className="text-right">{g.avgIterations}</TableCell>
                  <TableCell className="text-right">
                    <ScoreBadge score={g.avgReviewScore} />
                  </TableCell>
                  <TableCell className="text-right">{g.reviewsUsed}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="px-6 pb-6">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={sorted}
              layout="vertical"
              margin={{ left: 20, right: 20, top: 5, bottom: 5 }}
            >
              <XAxis type="number" domain={[0, 10]} tick={{ fill: 'hsl(var(--muted-foreground))' }} />
              <YAxis
                type="category"
                dataKey="groupName"
                width={140}
                tick={{ fill: 'hsl(var(--foreground))', fontSize: 13 }}
              />
              <Tooltip
                contentStyle={{
                  background: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '0.75rem',
                  color: 'hsl(var(--foreground))',
                }}
                formatter={(value: number) => [`${value.toFixed(1)}/10`, 'Ø Review-Score']}
              />
              <Bar dataKey="avgReviewScore" radius={[0, 6, 6, 0]} barSize={24}>
                {sorted.map((entry) => (
                  <Cell key={entry.groupId} fill={entry.color} fillOpacity={0.8} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </motion.div>
  );
};

export default GroupPerformancePanel;
